import { FormControlLabel, RadioGroup } from "@mui/material";
import classnames from "classnames";
import { type ChangeEvent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { calculatePrice } from "services/pricingModel/utils";
import { formatMoney } from "utils/money";
import { trackPricingItemSelection } from "services/subscriptions/analytics";
import { PlanRadio, RadioCheckedIcon, RadioIcon } from "../RadioStyles";
import { PricingModel, PricingModelItem } from "../../../services/pricingModel/model";
import styles from "./styles.module.scss";

interface PlanSelectorProps {
  pricingModel: PricingModel;
  selectedItem: PricingModelItem;
  setSelectedPlan: (item: PricingModelItem) => void;
  disabled?: boolean;
}

export default function PlanSelector({
  pricingModel,
  selectedItem: selectedPlan,
  setSelectedPlan,
  disabled,
}: PlanSelectorProps) {
  const { t } = useTranslation();

  const onChangePlan = useCallback(
    (newPlan: PricingModelItem) => {
      if (!disabled) {
        setSelectedPlan(newPlan);
        trackPricingItemSelection(newPlan);
      }
    },
    [disabled, setSelectedPlan]
  );

  const hdChangePlan = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.value && !disabled) {
        const plan = pricingModel.items.find(item => item.id === e.target.value);
        if (plan) {
          onChangePlan(plan);
        }
      }
    },
    [disabled, onChangePlan, pricingModel.items]
  );

  const selfStudyRadioGroup = pricingModel.items
    .filter(item => item.purchase_student_type === "self-study")
    .map(item => (
      <div
        className={classnames(
          styles.priceItem,
          selectedPlan.id === item.id && styles.active
        )}
        onClick={() => onChangePlan(item)}
        key={item.id}
      >
        <FormControlLabel
          disabled={disabled}
          value={item.id}
          control={
            <PlanRadio
              disabled={disabled}
              disableRipple
              color="default"
              checkedIcon={<RadioCheckedIcon />}
              icon={<RadioIcon />}
            />
          }
          label={item.name}
        />
        <div className={styles.priceTag}>
          <b>{formatMoney(item.price, item.currency)}</b>
          <span className={styles.perUnit}>
            {`${formatMoney(calculatePrice(item), item.currency)}${t("Common.comparePlans.perMonth")}`}
          </span>
        </div>
      </div>
    ));

  const oneOnOneRadioGroup = pricingModel.items
    .filter(item => item.purchase_student_type === "one-on-one")
    .map(item => (
      <div
        className={classnames(
          styles.priceItem,
          selectedPlan.id === item.id && styles.active
        )}
        onClick={() => setSelectedPlan(item)}
        key={item.id}
      >
        <FormControlLabel
          disabled={disabled}
          value={item.id}
          control={
            <PlanRadio
              disabled={disabled}
              disableRipple
              color="default"
              checkedIcon={<RadioCheckedIcon />}
              icon={<RadioIcon />}
            />
          }
          label={item.name}
        />
        <div className={styles.priceTag}>
          <b>{formatMoney(item.price, item.currency)}</b>
          <span className={styles.perUnit}>
            {`${formatMoney(calculatePrice(item), item.currency)}${t("Common.comparePlans.perSession")}`}
          </span>
        </div>
      </div>
    ));

  return (
    <RadioGroup
      defaultValue={selectedPlan.id}
      value={selectedPlan.id}
      onChange={hdChangePlan}
    >
      {selfStudyRadioGroup}
      {oneOnOneRadioGroup}
    </RadioGroup>
  );
}
